import { describeFilters, dateRangePart, basisPart } from './reportFilterSummary'
import { ALL } from './specializedFilterFields'
import { fmtDisplayDate } from '@utils/displayDate'
import { comparisonWindowOf, describeComparisonWindow } from '@pages/reports/plCompareWindow'
import { accountScopePart, ACCOUNT_TYPE_LABEL } from '@pages/reports/glAccountScope'

/**
 * CC-53435 (spec 185) — the criteria line of a SAVED specialized run, rebuilt from the
 * job's own stored params.
 *
 * ## Where this sits
 *
 * A `?jobId=` reopen of a specialized page renders the JOB's figures while the page's
 * filters are still its DEFAULTS (no page seeds its filters from the job — doing so
 * re-triggers `useAutoRunOnFilterChange` on Balance Sheet, Trial Balance, General
 * Ledger and P&L and replaces the saved figures with a fresh run). CC-53363 stopped the
 * documents from describing those defaults and made them say
 * `SAVED_RUN_CRITERIA_UNKNOWN` instead. This module supplies the TRUE line where the
 * job carries it, through the shell's existing `savedRunFiltersSummary` prop; the page
 * filter state is never touched.
 *
 * ## The decision is per JOB, not per page
 *
 * Measured on QA D1 `cb-report-jobs` (2026-09-02 and again 2026-10-15): the same report
 * id stores a full body for a job its page queued and a bare `{ method, path }` for one
 * created through a generic route. cb-api's `savedParamsOf` (jobs/routes.js) turns the
 * first into an object and the second into `null`. So every builder here answers for
 * the job in hand: a string when its params carry everything the page's LIVE line
 * states, `null` otherwise — and `null` makes the shell print the constant.
 *
 * ## The rules every builder follows
 *
 *  1. Same words as the page's LIVE line. Each builder calls the same
 *     `reportFilterSummary` parts (or, for P&L / Balance Sheet / General Ledger, the
 *     same helper) the page's live `filtersSummary` calls, in the same order, so a
 *     reopened run reads exactly like the run that produced it (MM/DD/YYYY, " · ",
 *     "Cash basis", …). The page-level tests pin live == reopened for each component.
 *  2. Never half-render. A part the live line states (a date, the basis, the zero
 *     toggle) that the job's params do not carry readably makes the WHOLE answer
 *     `null` — a line that silently drops the basis is a wrong document, not an
 *     incomplete one.
 *  3. Never claim less scope than the job ran with. The live lines of the CSV pages
 *     state the PERIOD only (CC-50029). A job scoped to one originator / client /
 *     matter / timekeeper stores only that object's id (or a name the live line never
 *     prints), and on a reopen nothing else on the page shows it — the filter bar holds
 *     the defaults. A period-only line over such a job reads as firm-wide, so those
 *     jobs answer `null`. The same holds for a narrowing toggle whose "no filter" value
 *     is unambiguous and which the live line does not state (Time Entries' Billed /
 *     Billable, Timekeeper Analysis' Invoice Posted). See spec 185 §4 for the one
 *     toggle deliberately NOT guarded (Aged A/R's Posted).
 *  4. Values arrive as cb-api returns them: a GET replay's params are its query
 *     string read back, so booleans are the STRINGS 'true' / 'false'; a POST replay's
 *     are its JSON body, so they are real booleans and numbers. Both are read; nothing
 *     else is guessed into a value.
 *
 * Pure: no I/O, no React. The queue drawer (`statementJobView.js`) uses the P&L and
 * Balance Sheet builders too, so a saved statement's criteria line is the same on the
 * drawer's PDF and on the reopened page's documents.
 */

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

/** A stored 'YYYY-MM-DD' that is a real calendar date (never 2026-02-31). */
export const isSavedDate = (v) => {
  if (typeof v !== 'string' || !ISO_DATE.test(v)) return false
  const d = new Date(`${v}T00:00:00Z`)
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v
}

/**
 * A stored boolean, as either replay kind returns it (rule 4). Anything else is
 * unknown — `null`, never a guessed basis.
 */
export const readSavedBool = (v) => {
  if (v === true || v === 'true') return true
  if (v === false || v === 'false') return false
  return null
}

const isParams = (sp) => sp != null && typeof sp === 'object' && !Array.isArray(sp)
const has = (sp, k) => Object.prototype.hasOwnProperty.call(sp, k)
const blank = (v) => v == null || String(v).trim() === ''

/**
 * Rule 3: every one of `keys` is PRESENT and empty. A key that is missing altogether is
 * not proof the run was unscoped — it is a shape this page never sends — so it fails.
 */
const unscoped = (sp, keys) => keys.every((k) => has(sp, k) && blank(sp[k]))

/**
 * Rule 3, for a toggle whose "no filter" value is known: present and equal to it. A
 * `null` "no filter" must be a real null — the string 'null' is not what any page sends.
 */
const atNoFilter = (sp, key, noFilter) => has(sp, key) && (noFilter === null
  ? sp[key] === null
  : sp[key] != null && String(sp[key]) === String(noFilter))

/** "MM/DD/YYYY to MM/DD/YYYY" from two stored dates, or null when either is unreadable. */
const savedRange = (start, end) => (isSavedDate(start) && isSavedDate(end) ? dateRangePart(start, end) : null)

// ---------------------------------------------------------------------------------
// P&L family (35 / 263 Profit & Loss, 180 / 236 Comparative P&L) — PLReport.jsx
// ---------------------------------------------------------------------------------

/**
 * CB's `PLComparePeriods` wire integers → enum NAMES, index = wire value (CC-49746).
 * The reverse of PLReport.jsx's PL_COMPARE_WIRE; `statementJobView.test.js` pins the
 * page-vs-drawer agreement.
 */
export const PL_COMPARE_NAME = ['None', 'PreviousPeriod', 'PreviousYear', 'YearToDate']

/** The compare period a saved P&L job ran with — the page's own reopen expression. */
export const savedPlCompare = (sp) => PL_COMPARE_NAME[Number(sp?.pLComparePeriods) || 0] || 'None'

/** A Category P&L job (a non-empty `filteredCategories` is what switches CB into it). */
export const isSavedCategoryPl = (sp) => String(sp?.filteredCategories ?? '') !== ''

/** 'PreviousYear' → 'Previous Year', as the page's criteria line spells it. */
const spacedCompareName = (name) => String(name).replace(/([a-z])([A-Z])/g, '$1 $2')

/**
 * PLReport.jsx `describePlCriteria`, from a saved job: the dates, the basis and — on a
 * comparison run — the window CB REPORTED for it (read from the stored `report`, the
 * same two fields the page's `compareWindow` reads). Moved here from
 * `statementJobView.js` (spec 127 §4.6), where the drawer has built it since PR 304.
 *
 * `null` for a Category P&L: its line must name the categories, and no Category P&L
 * job has ever been captured (vladdemo has category reporting gated off), so there is
 * nothing to pin a build against — the drawer refuses those jobs for the same reason.
 *
 * @param {object|null} savedParams the job's `savedParams`
 * @param {object|null} report      the stored result's `report` (for the comparison window)
 * @param {object} [opts]
 * @param {boolean} [opts.comparisonPage] the page rendering it. The plain P&L page never
 *   draws comparison columns (`isComparison && …`), so a job that asked for a
 *   comparison cannot be described there — `null` rather than a line naming columns
 *   the screen does not show. Omitted (the drawer): the job's own compare period rules.
 * @returns {string|null}
 */
export function pnlSavedCriteria(savedParams, report, { comparisonPage } = {}) {
  if (!isParams(savedParams)) return null
  const sp = savedParams
  if (isSavedCategoryPl(sp)) return null
  const ranCompare = savedPlCompare(sp)
  const comparisonActive = ranCompare !== 'None'
  if (comparisonPage === false && comparisonActive) return null
  const range = savedRange(sp.startDate, sp.endDate)
  const isCash = readSavedBool(sp.isCashBasis)
  if (!range || isCash == null) return null
  const window = comparisonActive ? comparisonWindowOf(report) : null
  return describeFilters(
    range,
    basisPart(isCash),
    comparisonActive
      ? `Compared to ${spacedCompareName(ranCompare)}${window ? ` (${describeComparisonWindow(window)})` : ''}`
      : null,
  )
}

// ---------------------------------------------------------------------------------
// Balance Sheet (34 / 372) — BalanceSheetReport.jsx
// ---------------------------------------------------------------------------------

/**
 * The categories a stored Balance Sheet result was run with, named from CB's own
 * columns: a Category Balance Sheet's CSV carries one column per selected category
 * between the four base headers and `Total` (ExportHelper.cs:3656; measured on
 * `bs-2026-09-28-accrual-category-all.route.json`, whose columns are exactly the
 * tenant's category names). Empty for an ordinary statement (`…, Amount`).
 */
export const savedBalanceSheetCategories = (headers) => {
  const h = Array.isArray(headers) ? headers : []
  return h.length > 5 && String(h[h.length - 1]).trim() === 'Total'
    ? h.slice(4, -1).map((x) => String(x).trim()).filter(Boolean)
    : []
}

/**
 * BalanceSheetReport.jsx's run line (`runFiltersLine`), from a saved job: as of, basis,
 * zero rows, categories. Moved verbatim from `statementJobView.js` (spec 127 §4.6) so the
 * drawer and the reopened page cannot drift.
 *
 * CATEGORIES — the one wording difference, settled for spec 185: a SAVED run names its
 * categories from CB's columns in the stored result, on the drawer AND on the reopened
 * page; a LIVE run keeps the page's list wording ("All" when every category is picked).
 * The stored result is the run's own record; the page's category list is TODAY's, and
 * "All" means "every category that exists now", which a saved run cannot claim.
 *
 * The layout part (side-by-side) is display state, not a run input — the page appends
 * it live, exactly as it does to a live run's line.
 *
 * @param {object|null} savedParams the job's `savedParams`
 * @param {string[]} headers        the stored result's headers
 * @returns {string|null}
 */
export function balanceSheetSavedCriteria(savedParams, headers) {
  const sp = isParams(savedParams) ? savedParams : {}
  const isCash = readSavedBool(sp.isCashBasis)
  if (!isSavedDate(sp.endDate) || isCash == null) return null
  const categories = savedBalanceSheetCategories(headers)
  return describeFilters(
    dateRangePart(null, sp.endDate),
    basisPart(isCash),
    readSavedBool(sp.isShowZeroRows) ? 'Including zero balances' : null,
    categories.length ? `Categories: ${categories.join(', ')}` : null,
  )
}

// ---------------------------------------------------------------------------------
// Trial Balance (147) — TrialBalanceReport.jsx
// ---------------------------------------------------------------------------------

/**
 * The page's live line, from a saved job: as of, basis, zero balances, sort.
 *
 * `sortBy` is the page's CURRENT sort, not the job's: the page sorts the lines itself
 * by `filters.sortBy` (CC-49348 — CB does not reliably apply its own), so the rows on
 * screen and in every export follow the panel's sort whatever the job asked CB for.
 * During a reopen the panel still holds what it had at mount, because changing Sort By
 * re-runs the report and ends the reopen.
 *
 * @param {object|null} savedParams the job's `savedParams`
 * @param {{ sortBy?: string }} [display] the page's display sort
 * @returns {string|null}
 */
export function trialBalanceSavedCriteria(savedParams, { sortBy } = {}) {
  if (!isParams(savedParams)) return null
  const sp = savedParams
  const isCash = readSavedBool(sp.isCashBasis)
  const zero = readSavedBool(sp.showZeroBal)
  if (!isSavedDate(sp.endDate) || isCash == null || zero == null) return null
  return describeFilters(
    dateRangePart(null, sp.endDate),
    basisPart(isCash),
    zero ? 'Including zero balances' : null,
    sortBy ? `Sorted by ${sortBy}` : null,
  )
}

// ---------------------------------------------------------------------------------
// General Ledger (363) — GeneralLedgerReport.jsx
// ---------------------------------------------------------------------------------

const ACCOUNT_IDS = /^-?\d+(\|-?\d+)*$/

/**
 * The explicit account ids a saved General Ledger job ran with ([] for an account-type
 * or all-accounts run). The page reads the chart of accounts while this is non-empty,
 * so it can NAME them exactly as its live line does.
 */
export const savedGlAccountIds = (savedParams) => {
  if (!isParams(savedParams) || blank(savedParams.filteredAccounts)) return []
  const ids = String(savedParams.filteredAccounts)
  return ACCOUNT_IDS.test(ids) ? ids.split('|') : []
}

/**
 * The page's live line, from a saved job: dates, basis, zero balances, and the account
 * scope through the page's own `accountScopePart` — "All account types", "Account types:
 * Bank", or each specific account BY NAME (CC-50326 / CC-53890).
 *
 * `null` when the stored scope is not one the page can send: both an id list and a type
 * token (`toGlWire` never sends both), an id list that is not ids, or a type value the
 * page does not offer. A job with neither key ran every account — `buildQs` drops the
 * empty `accountCategories` the page sends for "All".
 *
 * @param {object|null} savedParams the job's `savedParams`
 * @param {Map|null} labelsById     account id → picker label (null: not loaded / unreadable)
 * @returns {string|null}
 */
export function generalLedgerSavedCriteria(savedParams, labelsById = null) {
  if (!isParams(savedParams)) return null
  const sp = savedParams
  const range = savedRange(sp.startDate, sp.endDate)
  const isCash = readSavedBool(sp.isCashBasis)
  const zero = readSavedBool(sp.showZeroBal)
  if (!range || isCash == null || zero == null) return null
  const accounts = blank(sp.filteredAccounts) ? null : String(sp.filteredAccounts)
  const types = blank(sp.accountCategories) ? null : String(sp.accountCategories)
  if (accounts && types) return null
  if (accounts && !ACCOUNT_IDS.test(accounts)) return null
  if (types && types.split('|').some((v) => !ACCOUNT_TYPE_LABEL.has(v))) return null
  return describeFilters(
    range,
    basisPart(isCash),
    zero ? 'Including zero balances' : null,
    accountScopePart({ accountSelection: accounts ?? ALL, accountCategories: types ?? '' }, labelsById),
  )
}

// ---------------------------------------------------------------------------------
// The CSV-backed pages — their live lines state the period only (CC-50029)
// ---------------------------------------------------------------------------------

/**
 * Aged A/R (21 / 227) — ARReport.jsx `arRunLine`: "As of MM/DD/YYYY". The page appends
 * its DISPLAY parts (practice area, zero / inactive hidden) live, as it does to a live
 * run's line, because they re-filter the rows on screen whatever run produced them.
 */
export function arSavedCriteria(savedParams) {
  if (!isParams(savedParams)) return null
  const sp = savedParams
  if (!isSavedDate(sp.endDate)) return null
  if (!unscoped(sp, ['companyId', 'origId', 'matterId', 'timekeeperId'])) return null
  return `As of ${fmtDisplayDate(sp.endDate)}`
}

/** Fee Receipts by Originators (22) — the Payment Received window. */
export function feeReceiptsSavedCriteria(savedParams) {
  if (!isParams(savedParams)) return null
  const sp = savedParams
  if (!unscoped(sp, ['userId'])) return null
  const range = savedRange(sp.paymentReceivedDateStart, sp.paymentReceivedDateEnd)
  return range ? describeFilters(range) : null
}

/**
 * Timekeeper Analysis (26 / 230 / 231). The stored body is the TRANSLATED wire
 * (`toTkaWireFilter`: entryDateStart → dateStart, clientId → companyId, userId →
 * userIds), so the saved keys differ from the page's filter keys.
 */
export function timekeeperAnalysisSavedCriteria(savedParams) {
  if (!isParams(savedParams)) return null
  const sp = savedParams
  if (!unscoped(sp, ['companyId', 'userIds'])) return null
  // Invoice Posted: null is "Don't filter" (the page's own encoding, POSTED_OPTIONS).
  if (!atNoFilter(sp, 'postedFilter', null)) return null
  const range = savedRange(sp.dateStart, sp.dateEnd)
  return range ? describeFilters(range) : null
}

/** Time Entries (38). Billed / Billable: 0 is "Both" on the wire (the page's own encoding). */
export function timeEntriesSavedCriteria(savedParams) {
  if (!isParams(savedParams)) return null
  const sp = savedParams
  if (!unscoped(sp, ['clientId', 'userId', 'respAttyId', 'billingObjectId'])) return null
  if (!atNoFilter(sp, 'isBilled', 0) || !atNoFilter(sp, 'isBillable', 0)) return null
  const range = savedRange(sp.entryDateStart, sp.entryDateEnd)
  return range ? describeFilters(range) : null
}

/** Monthly Productivity (23) — "Year YYYY". */
export function monthlyProductivitySavedCriteria(savedParams) {
  if (!isParams(savedParams)) return null
  const sp = savedParams
  if (!unscoped(sp, ['companyId', 'userId'])) return null
  const year = String(sp.year ?? '')
  return /^\d{4}$/.test(year) ? describeFilters(`Year ${year}`) : null
}
